// The WRITE half of `/v1/dashboards` (#1033) — create / rename / delete. Split
// from `dashboards.ts` (the reads) so each file keeps one reason to change and
// both stay under the 150-LOC rule; all three routes answer the same
// `DashboardDetail` the read side already models.
//
// Nothing here patches a cache. Every caller re-READS (list refetch / detail
// refetch) once the API has accepted the write — the POST's echo is what the
// server *did*, not what the account now looks like to this viewer.

import type { UpsertDashboardWidgetInput } from '@developerz/domain';
import { apiFetch } from './api';
import type { DashboardDetail, DashboardSummary } from './dashboards';
import { SESSION_TOKEN } from './session';

/** The two scopes a caller may AUTHOR. `custom` is derived server-side from
 * `builtin`, never sent — a hand-authored board is custom by construction. */
export type AuthoredScope = 'org' | 'user';
export const AUTHORED_SCOPES: readonly AuthoredScope[] = ['org', 'user'];

/** `POST /v1/dashboards` body — `CreateDashboardBodySchema` (apps/api). `.strict()`,
 * so an unknown field is a 400. The schema's fourth member, `replace` (default
 * `false`, #1099), is deliberately absent here: opting in reuses the colliding row
 * and DROPS its widgets, and this form never asks for that. */
export type CreateDashboardBody = {
  readonly name: string;
  readonly scope: AuthoredScope;
  readonly widgets: readonly UpsertDashboardWidgetInput[];
};

/** What the create form collects. `note` becomes the board's first widget. */
export type NewBoardForm = {
  readonly name: string;
  readonly scope: AuthoredScope;
  readonly note: string;
};

/**
 * Form → create body. The contract demands at least one widget (a layout needs
 * ≥1 row, so an empty board is unrepresentable), and MARKDOWN is the one kind
 * that carries no query, no capability negotiation and no column keys — the
 * minimum valid board a form can author. Data widgets stay API/MCP-authored: a
 * widget designer is a different feature, and a half-one would let a maintainer
 * author a board the resolver can't page.
 */
export function noteBoardBody(form: NewBoardForm): CreateDashboardBody {
  const name = form.name.trim();
  return {
    name,
    scope: form.scope,
    widgets: [{ spec: { kind: 'markdown', title: name, content: form.note.trim() }, span: 12 }],
  };
}

/**
 * Does `form` name a board the caller can ALREADY SEE? `POST /v1/dashboards` is
 * keyed on `(account, owner, name)`, and a collision now 409s server-side unless
 * the body opts into `replace: true` (#1099) — this form never sends it, so the
 * destructive path is unreachable from here. The mirror survives that fix because
 * it answers BEFORE the round-trip, naming the board in the caller's own list
 * instead of bouncing them off an error. It is NOT enforcement: the server stays
 * the authority, and only IT sees boards this caller can't.
 *
 * The owner half is `owner_user_id`, never the router `scope`: that is `custom`
 * for every authored board, personal or shared (`dashboardScope`). The list is
 * already shared ∪ own, so a non-null owner IS this caller — which is exactly
 * who `scope: 'user'` writes as, while `scope: 'org'` writes with no owner.
 */
export function boardNameTaken(
  boards: readonly Pick<DashboardSummary, 'name' | 'owner_user_id'>[],
  form: Pick<NewBoardForm, 'name' | 'scope'>,
): boolean {
  const name = form.name.trim();
  const personal = form.scope === 'user';
  return boards.some((b) => b.name === name && (b.owner_user_id !== null) === personal);
}

/** Create a board. 409 when the name is already taken — a seeded built-in, or any
 * board on the same `(account, owner, name)` (#1099); 403 when `scope: 'user'` is
 * asked for without a user-bound session — both carry a `detail` the caller
 * renders inline. */
export async function createDashboard(
  body: CreateDashboardBody,
  token: string | null = SESSION_TOKEN,
): Promise<DashboardDetail> {
  return apiFetch<DashboardDetail>('/v1/dashboards', token, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Rename a board. The layout half of `PATCH` is deliberately not wired: the SPA
 * renders layouts, it doesn't author them. 409 on a built-in. */
export async function renameDashboard(
  id: string,
  name: string,
  token: string | null = SESSION_TOKEN,
): Promise<DashboardDetail> {
  return apiFetch<DashboardDetail>(`/v1/dashboards/${encodeURIComponent(id)}`, token, {
    method: 'PATCH',
    body: JSON.stringify({ name: name.trim() }),
  });
}

/** Delete a board and cascade its widgets. 409 on a built-in (shared state). */
export async function deleteDashboard(
  id: string,
  token: string | null = SESSION_TOKEN,
): Promise<void> {
  await apiFetch<{ id: string; deleted: boolean }>(
    `/v1/dashboards/${encodeURIComponent(id)}`,
    token,
    { method: 'DELETE' },
  );
}
